import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Clock, ChevronRight, History, RotateCcw, Loader2, ArrowLeft } from 'lucide-react';
import { supplementsApi } from '../api';

function formatDate(iso, language) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const locale = language === 'pt' ? 'pt-PT' : 'en-GB';
    return d.toLocaleString(locale, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function summaryToText(t, summary) {
  if (!summary) return t('supplementPlan.history.summary.planEdited', 'Plan edited');
  if (summary.startsWith('restoredToVersion:')) {
    const n = summary.split(':')[1];
    return t('supplementPlan.history.summary.restoredToVersion', { n, defaultValue: `Restored to v${n}` });
  }
  switch (summary) {
    case 'planEdited':                 return t('supplementPlan.history.summary.planEdited', 'Plan edited');
    case 'planRenamed':                return t('supplementPlan.history.summary.planRenamed', 'Plan renamed');
    case 'supplementsChanged':         return t('supplementPlan.history.summary.supplementsChanged', 'Supplements changed');
    case 'supplementsAddedOrRemoved':  return t('supplementPlan.history.summary.supplementsAddedOrRemoved', 'Supplements added or removed');
    case 'dosagesChanged':             return t('supplementPlan.history.summary.dosagesChanged', 'Dosages changed');
    case 'notesChanged':               return t('supplementPlan.history.summary.notesChanged', 'Notes changed');
    case 'planDeleted':                return t('supplementPlan.history.summary.planDeleted', 'Plan deleted');
    case 'restoredFromTrash':          return t('supplementPlan.history.summary.restoredFromTrash', 'Restored from trash');
    default:                           return summary;
  }
}

/**
 * Supplement plan version history. Mirrors RoutineHistoryDialog:
 * version list on the left, snapshot preview on the right, restore from a version.
 *
 * Props:
 *  - open, planId, currentPlan, onClose, onRestored
 */
export default function SupplementPlanHistoryDialog({ open, planId, currentPlan = null, onClose, onRestored }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedVersion, setSelectedVersion] = useState('current');
  const [confirmRestore, setConfirmRestore] = useState(false);

  const versionsQuery = useQuery({
    queryKey: ['supplement-plan-versions', planId],
    queryFn: async () => (await supplementsApi.listPlanVersions(planId)).data,
    enabled: !!(open && planId),
  });

  const detailQuery = useQuery({
    queryKey: ['supplement-plan-version', planId, selectedVersion],
    queryFn: async () => (await supplementsApi.getPlanVersion(planId, selectedVersion)).data,
    enabled: !!(open && planId && typeof selectedVersion === 'number'),
  });

  useEffect(() => {
    if (!open) setSelectedVersion('current');
  }, [open]);

  const restoreMutation = useMutation({
    mutationFn: () => supplementsApi.restorePlanVersion(planId, selectedVersion),
    onSuccess: () => {
      toast.success(t('supplementPlan.history.restoredToast', 'Version restored'));
      queryClient.invalidateQueries({ queryKey: ['supplement-plan-versions', planId] });
      setConfirmRestore(false);
      onRestored?.();
      onClose?.();
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('common.error', 'Error'));
    },
  });

  const versions = versionsQuery.data || [];
  const isCurrent = selectedVersion === 'current';
  const previewPlan = isCurrent ? currentPlan : detailQuery.data?.snapshot;

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
        <SheetContent side="right" className="w-full sm:max-w-3xl p-0 flex flex-col" data-testid="supplement-plan-history-dialog">
          <SheetHeader className="px-4 py-3 border-b border-border/30 flex-row items-center gap-3 space-y-0 flex-shrink-0">
            <History className="h-5 w-5 text-primary flex-shrink-0" />
            <SheetTitle className="text-base font-bold flex-1 min-w-0 truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('supplementPlan.history.title', 'Version history')}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
            {/* Version list */}
            <div className={`md:w-80 md:border-r md:border-border/30 flex flex-col min-h-0 ${selectedVersion != null ? 'hidden md:flex' : 'flex'}`}>
              <ScrollArea className="flex-1 min-h-0">
                {versionsQuery.isLoading && (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                )}
                <ul className="divide-y divide-border/30">
                  {currentPlan && (
                    <li>
                      <button
                        type="button"
                        onClick={() => setSelectedVersion('current')}
                        className={`w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex items-start gap-3 ${isCurrent ? 'bg-muted/60' : ''}`}
                        data-testid="supplement-version-row-current"
                      >
                        <Clock className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <Badge variant="outline" className="h-4 px-1.5 text-[10px] bg-primary/10 text-primary border-primary/40 mb-0.5">
                            {t('supplementPlan.history.current', 'Current')}
                          </Badge>
                          <p className="text-sm font-medium truncate">{currentPlan.name}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0 hidden md:block" />
                      </button>
                    </li>
                  )}
                  {!versionsQuery.isLoading && versions.length === 0 && (
                    <li className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {t('supplementPlan.history.empty', 'No previous versions yet')}
                    </li>
                  )}
                  {versions.map((v) => (
                    <li key={v.version_number}>
                      <button
                        type="button"
                        onClick={() => setSelectedVersion(v.version_number)}
                        className={`w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex items-start gap-3 ${selectedVersion === v.version_number ? 'bg-muted/60' : ''}`}
                        data-testid={`supplement-version-row-${v.version_number}`}
                      >
                        <Clock className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-xs font-mono text-muted-foreground">v{v.version_number}</span>
                          <p className="text-sm font-medium truncate">{summaryToText(t, v.change_summary)}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {formatDate(v.created_at, i18n.language)}
                            {v.author_name ? ` · ${v.author_name}` : ''}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0 hidden md:block" />
                      </button>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            </div>

            {/* Preview */}
            <div className={`flex-1 min-h-0 flex flex-col ${selectedVersion != null ? 'flex' : 'hidden md:flex'}`}>
              <div className="px-4 py-3 border-b border-border/30 flex items-center gap-2 flex-shrink-0">
                <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSelectedVersion(null)} tooltip={t('common.back', 'Back')}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold truncate">
                    {isCurrent
                      ? `${t('supplementPlan.history.current', 'Current')}${previewPlan?.name ? ` · ${previewPlan.name}` : ''}`
                      : `v${selectedVersion}${previewPlan?.name ? ` · ${previewPlan.name}` : ''}`}
                  </p>
                  {!isCurrent && detailQuery.data?.created_at && (
                    <p className="text-xs text-muted-foreground truncate">
                      {formatDate(detailQuery.data.created_at, i18n.language)}
                    </p>
                  )}
                </div>
                {!isCurrent && (
                  <Button
                    onClick={() => setConfirmRestore(true)}
                    disabled={!detailQuery.data || restoreMutation.isPending}
                    data-testid="supplement-version-restore-btn"
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                    {t('supplementPlan.history.restoreThisVersion', 'Restore this version')}
                  </Button>
                )}
              </div>
              <ScrollArea className="flex-1 min-h-0">
                {!isCurrent && detailQuery.isLoading && (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                )}
                {previewPlan && (
                  <div className="px-4 py-3 space-y-2">
                    {previewPlan.notes && (
                      <p className="text-sm text-muted-foreground">{previewPlan.notes}</p>
                    )}
                    <ul className="rounded-lg border border-border/40 divide-y divide-border/20 overflow-hidden">
                      {(previewPlan.supplements || []).map((s, i) => (
                        <li key={s.supplement_id || i} className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm flex-1 min-w-0 truncate">{s.supplement_name}</span>
                            <span className="text-xs text-muted-foreground flex-shrink-0">{s.dosage}</span>
                          </div>
                          {(s.times?.length > 0) && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {s.times.map((time, ti) => (
                                <span key={ti} className="text-[11px] px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground whitespace-nowrap">
                                  {time}
                                </span>
                              ))}
                            </div>
                          )}
                        </li>
                      ))}
                      {(previewPlan.supplements || []).length === 0 && (
                        <li className="px-3 py-4 text-center text-sm text-muted-foreground">
                          {t('supplementPlan.history.noSupplements', 'No supplements')}
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmRestore} onOpenChange={setConfirmRestore}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('supplementPlan.history.restoreConfirmTitle', 'Restore this version?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('supplementPlan.history.restoreConfirmDescription', 'The current version will be saved automatically and can itself be restored.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoreMutation.isPending}>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); restoreMutation.mutate(); }}
              disabled={restoreMutation.isPending}
              data-testid="supplement-version-restore-confirm"
            >
              {restoreMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t('supplementPlan.history.restoreThisVersion', 'Restore this version')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
